import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuItems } from '../../../enums/MenuItems'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { RoleTemplateRegister } from './roleTemplates/RoleTemplateRegister'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import roleApi from '../../../api/role/RoleApi'
import { Pages } from '../../../Pages'
import Button from '../../common/form/inputs/Button'
import LlmPromptInput from '../../common/llm/LlmPromptInput'

export default function CreateRoleLLM(): React.JSX.Element {
    const [prompt, setPrompt] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [apiError, setApiError] = useState<string | undefined>(undefined)
    const navigate = useNavigate()

    const submitPrompt = useCallback(async () => {
        const trimmed = prompt.trim()
        if (!trimmed || isLoading) return
        setApiError(undefined)
        setIsLoading(true)
        let shouldResetLoading = true

        try {
            const roleRes = await roleApi.createRoleViaLlm({
                llmInput: trimmed,
            })
            navigate(`${Pages.ViewRole}?id=${roleRes.roleId}`)
            shouldResetLoading = false
        } catch (error) {
            console.error(error)
            const message =
                error instanceof Error
                    ? error.message
                    : 'An unknown error occurred. Please try again later.'
            setApiError(message)
        } finally {
            if (shouldResetLoading) {
                setIsLoading(false)
            }
        }
    }, [prompt, isLoading, navigate])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault()
            submitPrompt()
        }
    }

    const handleTemplateClick = (templateId: string) => {
        const template = RoleTemplateRegister.getTemplateById(templateId)
        setPrompt(template.jobDescription)
        setApiError(undefined)
    }

    const templates = RoleTemplateRegister.templates
    const isSubmitDisabled = prompt.trim().length === 0 || isLoading

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner
                    show={true}
                    fullScreen={true}
                    captions={[
                        {
                            caption: 'Preparing new role...',
                        },
                    ]}
                />
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView
            activeMenuItem={MenuItems.Roles}
            className="!pb-0 h-full"
        >
            <div className="flex flex-col h-full mx-4">
                <div className="h-[20vh] flex-none"></div>
                <div className="">
                    <div className="text-3xl text-center">
                        Paste a job description or describe the role
                    </div>
                    <div className="mt-8">
                        <LlmPromptInput
                            textareaId="rolePrompt"
                            value={prompt}
                            onChange={(nextValue) => {
                                setPrompt(nextValue)
                                if (apiError) {
                                    setApiError(undefined)
                                }
                            }}
                            onSubmit={submitPrompt}
                            onKeyDown={handleKeyDown}
                            disableSubmit={isSubmitDisabled}
                        />
                    </div>
                    <div className="flex justify-center mt-4">
                        <div className="w-full md:w-2/3">
                            {templates.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {templates.map((template) => (
                                        <Button
                                            key={template.id}
                                            style="secondary"
                                            type="button"
                                            onClick={() =>
                                                handleTemplateClick(template.id)
                                            }
                                            // size="small"
                                            classes="font-normal"
                                        >
                                            {template.name}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutSingleView>
    )
}
